import { Logo, SourceGlyph, cx } from '@flexbyt/widget-preview';
import { Link } from '@tanstack/react-router';
import { FOOTER_COLUMNS, LEGAL_LINKS } from '../../content/navigation.js';
import { CONNECT_SOURCES } from '../../content/sources.js';
import { site } from '../../lib/site.js';
import { GUTTER } from '../ui/layout.js';

/**
 * The footer.
 *
 * ---------------------------------------------------------------------------
 * It is a site map, and it is read
 * ---------------------------------------------------------------------------
 * On a site built for search, the footer is where somebody who landed on one
 * deep page discovers there are forty others. It is also most of the internal
 * linking the brief asks for — every page linking to the sources, the widgets
 * and the platform pages is exactly the structure that makes a set of pages
 * read as a site rather than a pile.
 *
 * So it is dense on purpose. Five columns of real links, not three columns and
 * a newsletter box.
 *
 * ---------------------------------------------------------------------------
 * Night, like flexbyt.com
 * ---------------------------------------------------------------------------
 * The same ground, grain and blue/grape glow as the parent company's footer,
 * so the two sites close the same way. The oversized wordmark bleeding off the
 * bottom edge is decoration only and hidden from assistive tech.
 */
export function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="on-ink grain relative overflow-hidden border-t border-white/[0.08] bg-night text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 -top-40 -z-10 size-[36rem] rounded-full bg-brand/25 blur-[140px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-1/3 -z-10 size-[32rem] rounded-full bg-grape/25 blur-[140px]"
      />

      <div className={cx('mx-auto max-w-page pt-20 md:pt-24', GUTTER)}>
        <div className="grid gap-12 md:grid-cols-[minmax(0,1fr)_2.4fr] md:gap-12">
          {/* Identity */}
          <div className="max-w-xs">
            <Link to="/" className="inline-block text-white" aria-label="ReviewsJet — home">
              <Logo className="h-8" />
            </Link>

            <p className="mt-5 text-body-sm leading-7 text-white/55">
              Bring your reviews together, manage them in one place, and display
              your best ones anywhere on your website.
            </p>

            <a
              href={`mailto:${site.email}`}
              className="mt-7 inline-block text-xl font-extrabold tracking-[-0.03em] text-white underline decoration-white/25 decoration-2 underline-offset-[6px] transition-colors hover:decoration-white"
            >
              {site.email}
            </a>

            {/*
              The source marks, small and quiet. They say what ReviewsJet reads
              from faster than the "Review sources" column does. Set on white
              chips so their own brand colours survive the dark ground.
            */}
            <ul className="mt-7 flex flex-wrap items-center gap-2">
              {CONNECT_SOURCES.map((source) => (
                <li
                  key={source.id}
                  className="flex size-8 items-center justify-center rounded-lg bg-white/95"
                >
                  <SourceGlyph source={source.id} className="size-4" />
                  <span className="sr-only">{source.name}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-5">
            {FOOTER_COLUMNS.map((column) => (
              <nav key={column.heading} aria-labelledby={`footer-${slug(column.heading)}`}>
                <h2
                  id={`footer-${slug(column.heading)}`}
                  className="text-body-sm font-bold text-white"
                >
                  {column.heading}
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {column.links.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className={cx('text-body-sm', FOOTER_LINK)}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        {/* Legal */}
        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 py-7 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-caption text-white/45">
            © {year} ReviewsJet. All rights reserved.
          </p>

          <nav aria-label="Legal">
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {LEGAL_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={cx('text-caption', FOOTER_LINK)}>
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a href={`mailto:${site.email}`} className={cx('text-caption', FOOTER_LINK)}>
                  Contact
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Oversized wordmark that bleeds off the bottom edge */}
      <div
        aria-hidden
        className="pointer-events-none -mb-[0.22em] select-none whitespace-nowrap text-center text-[17vw] font-extrabold leading-[0.8] tracking-[-0.07em] text-white/[0.06]"
      >
        ReviewsJet
      </div>
    </footer>
  );
}

const FOOTER_LINK =
  'rounded-sm text-white/65 transition-colors duration-[var(--duration-fast)] hover:text-white';

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}
